// A lost response is not proof that the server did not save the document.
// Only these upload preflight gates run before the S3/database write starts.
const PREWRITE_CODES = new Set([
    's3_mutation_gate_path_missing',
    's3_mutation_gate_case_path_mismatch',
    's3_mutation_gate_unavailable',
    's3_mutation_gate_busy',
    's3_mutation_gate_check_failed',
    'folder_rename_path_locked',
    'case_archive_import_path_locked',
]);
const PHP_UPLOAD_ERRORS = new Set([1, 2, 3, 4, 6, 7, 8]);
// Both receipt gates reject single-file uploads/folder creation before any
// S3 or database write. They are a known rejection, not a lost response.
const DOCUMENT_OPERATION_LOCK_CODES = new Set([
    'document_transfer_path_locked',
    'document_delete_path_locked',
]);
// These versioned skip-existing responses guarantee that no destination was
// committed. A conditional 409 is retryable; a lost conditional-write response
// is deliberately NOT in this allowlist, even if it carries empty file IDs.
const SKIP_EXISTING_REJECTIONS = new Map([
    ['upload_destination_invalid', 400],
    ['upload_destination_unavailable', 503],
    ['upload_existing_lookup_failed', 503],
    ['upload_skip_unsupported', 503],
    ['upload_conditional_conflict', 409],
    ['upload_conflict_policy_invalid', 400],
]);
const PREWRITE_MESSAGES = new Map([
    ['Missing case ID', 200],
    ['Missing required parameters', 200],
    ['Root-level folder creation is not allowed for this case type. Please use Generate Folders.', 200],
    ['Authentication required', 401],
    ['Invalid CSRF token', 403],
    ['Case not found or access denied', 403],
    ['Case access is temporarily unavailable', 503],
    ['The lead does not belong to the selected case', 400],
    ['Invalid custom field identifier', 400],
    ['The upload field is not available for modification', 403],
    ['Invalid field group identifier', 400],
]);

export class DocumentUploadOutcomeError extends Error {
    constructor(message, { status = 0, uploadOutcome = 'unknown', retryable = false } = {}) {
        super(message);
        this.name = 'DocumentUploadOutcomeError';
        this.status = status;
        this.uploadOutcome = uploadOutcome;
        this.retryable = retryable;
    }
}

export const unknownDocumentUploadError = (operation = 'file', status = 0) => (
    new DocumentUploadOutcomeError(
        `The server response was interrupted or unreadable. CaseDB could not confirm whether this ${operation === 'folder' ? 'folder' : 'file'} was saved. Check the original destination before retrying.`,
        { status },
    )
);

export const documentUploadResponseError = (payload, status = 0, operation = 'file') => {
    const hasSavedFiles = Array.isArray(payload?.files) && payload.files.length > 0;
    if (status === 409 && payload?.success === false && !hasSavedFiles
        && DOCUMENT_OPERATION_LOCK_CODES.has(payload.error_code)) {
        return new DocumentUploadOutcomeError(
            `The destination is still finishing an earlier document action. This ${operation === 'folder' ? 'folder' : 'file'} was not saved. Try again shortly.`,
            { status, uploadOutcome: 'rejected', retryable: true },
        );
    }
    if (operation === 'file' && payload?.success === false
        && payload.upload_conflict_policy_version === 1 && payload.outcome_unknown === false
        && typeof payload.retryable === 'boolean'
        && Array.isArray(payload.files) && payload.files.length === 0
        && SKIP_EXISTING_REJECTIONS.get(payload.error_code) === status) {
        return new DocumentUploadOutcomeError(
            payload.retryable
                ? 'The server did not save this file. Retry with Skip existing files.'
                : 'The server rejected this upload before saving it. Check the destination and skip-existing support before retrying.',
            { status, uploadOutcome: 'rejected', retryable: payload.retryable },
        );
    }
    const sessionRejected = status === 401 && payload?.requires_login === true
        && ['Authentication required', 'Session expired'].includes(payload.error);
    const prewriteRejected = payload?.success === false && !hasSavedFiles && (
        sessionRejected
        || PREWRITE_CODES.has(payload.error_code)
        || (operation === 'file' && PHP_UPLOAD_ERRORS.has(payload.error_code))
        || (PREWRITE_MESSAGES.has(payload.message) && PREWRITE_MESSAGES.get(payload.message) === status)
    );
    if (!prewriteRejected) return unknownDocumentUploadError(operation, status);
    const message = sessionRejected ? 'Sign in again before retrying this upload.'
        : typeof payload.message === 'string' && payload.message.trim()
        ? payload.message.slice(0, 500)
        : 'The server rejected this upload before saving it.';
    return new DocumentUploadOutcomeError(message, { status, uploadOutcome: 'rejected', retryable: true });
};

export const parseDocumentUploadResponse = (text, status = 0, operation = 'file') => {
    let payload;
    try { payload = JSON.parse(text); }
    catch (_) { throw unknownDocumentUploadError(operation, status); }
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)
        || typeof payload.success !== 'boolean') {
        throw unknownDocumentUploadError(operation, status);
    }
    return payload;
};
